import { useMutation, useQueryClient } from '@tanstack/react-query';

import { RAIL_EUROPE_SEARCH_STALE_TIME } from '~constants/pointToPointTicket';
import { transportationApi } from '~endpoints/Transportation';
import { pointToPointTicketKeys } from '~queryKeys/transportation/pointToPointTicket';
import extractExpireTime from '~utils/pointToPointTicket/extractExpireTime';
import { mapDataWithSearchId } from '~utils/pointToPointTicket/legSolutionsMapper';

import type { SearchPointToPointTicketParam } from '~models/Transportation/PointToPointTicket';

export async function searchSolution(params: SearchPointToPointTicketParam, signal?: AbortSignal) {
  return transportationApi.v1.RailEurope.search(params, signal);
}

const useOutboundSearch = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (params: SearchPointToPointTicketParam & { signal?: AbortSignal }) => {
      const { signal, ...rest } = params;
      return searchSolution(rest, signal);
    },
    onSuccess: ({ data }) => {
      const resultData = mapDataWithSearchId(data);
      const { correlationId, expire } = resultData;

      queryClient.setQueryData(
        pointToPointTicketKeys.getLegSolutionFirstList(correlationId, undefined),
        resultData,
      );
      queryClient.setQueryDefaults(
        pointToPointTicketKeys.getLegSolutionFirstList(correlationId, undefined),
        {
          staleTime: extractExpireTime(expire) ?? RAIL_EUROPE_SEARCH_STALE_TIME,
        },
      );

      return resultData;
    },
  });

  return {
    ...mutation,
    mutateAsync: async (
      params: SearchPointToPointTicketParam & { signal?: AbortSignal },
      options?: any,
    ) => {
      return mutation.mutateAsync(params, {
        ...options,
        onSuccess: (data) => {
          if (options?.onSuccess) {
            options.onSuccess(mapDataWithSearchId(data.data));
          }
        },
        onError: options?.onError,
      });
    },
  };
};

export default useOutboundSearch;
